import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import AppLayout from 'layouts/app-layout';
import AuthLayout from 'layouts/auth-layout';
import AppLocale from 'lang';
import { IntlProvider } from 'react-intl';
import { ConfigProvider } from 'antd';
import Loading from 'components/shared-components/Loading';
import ErrorBoundary from 'components/shared-components/ErrorBoundary';
import ComponentErrorBoundary from 'components/shared-components/ComponentErrorBoundary';
import { APP_PREFIX_PATH, AUTH_PREFIX_PATH } from 'configs/AppConfig';
import useBodyClass from 'hooks/useBodyClass';

//main
function ProtectedRoute({ children, isAuthenticated }) {
  const location = useLocation();

  // If not authenticated, redirect immediately
  if (!isAuthenticated) {
    // Store the intended path for after authentication
    if (
      location.pathname !== '/auth/login' &&
      location.pathname !== '/auth/callback'
    ) {
      sessionStorage.setItem(
        'intended_path',
        location.pathname + location.search,
      );
    }

    return (
      <Navigate to={AUTH_PREFIX_PATH} state={{ from: location }} replace />
    );
  }

  return children;
}

export const Views = (props) => {
  const { locale, direction, token } = props;
  const location = useLocation();
  const dispatch = useDispatch();
  const currentAppLocale = AppLocale[locale];
  const [isInitialized, setIsInitialized] = useState(false);

  useBodyClass(`dir-${direction}`);

  useEffect(() => {
    // Simple initialization without delay
    setIsInitialized(true);
  }, []);

  // Show loading only during initial mount
  if (!isInitialized) {
    return <Loading cover="page" />;
  }

  return (
    <IntlProvider
      locale={currentAppLocale.locale}
      messages={currentAppLocale.messages}
    >
      <ConfigProvider
        locale={currentAppLocale.antd}
        direction={direction}
        theme={{
          token: {
            colorPrimary: '#7f0151', // Deep purple color
            colorPrimaryHover: '#5c003a', // Darker purple on hover
            colorPrimaryActive: '#5c003a', // Darker purple when pressed
          },
        }}
        notification={{
          placement: 'topRight',
          maxCount: 3,
        }}
        message={{
          maxCount: 3,
          duration: 2,
        }}
      >
        <Routes>
          <Route
            path="/"
            element={
              token !== null && token !== undefined ? (
                <Navigate to={`${APP_PREFIX_PATH}/dashboard`} replace />
              ) : (
                <Navigate to={`${AUTH_PREFIX_PATH}/login`} replace />
              )
            }
          />

          <Route
            path={`${AUTH_PREFIX_PATH}/*`}
            element={
              <ComponentErrorBoundary
                title="Authentication Error"
                message="There was an issue loading the authentication page."
              >
                <AuthLayout direction={direction} />
              </ComponentErrorBoundary>
            }
          />

          <Route
            path={`${APP_PREFIX_PATH}/*`}
            element={
              <ProtectedRoute isAuthenticated={token}>
                <ComponentErrorBoundary
                  title="Application Error"
                  message="There was an issue loading the main application."
                >
                  <AppLayout direction={direction} location={location} />
                </ComponentErrorBoundary>
              </ProtectedRoute>
            }
          />

          {/* Catch-all route for invalid URLs */}
          <Route
            path="*"
            element={
              token !== null && token !== undefined ? (
                <Navigate to={`${APP_PREFIX_PATH}/dashboard`} replace />
              ) : (
                <Navigate to={`${AUTH_PREFIX_PATH}/login`} replace />
              )
            }
          />
        </Routes>
      </ConfigProvider>
    </IntlProvider>
  );
};

const mapStateToProps = ({ theme, auth }) => {
  const { locale, direction } = theme;
  const { token } = auth;
  return { locale, token, direction };
};

export default connect(mapStateToProps)(Views);
